import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { shopify, inventory, rto } from '../services/api';
import {
  ShoppingBag, CheckCircle, IndianRupee, RotateCcw,
  AlertTriangle, RefreshCw, CreditCard, Banknote,
  Calendar, ChevronLeft, ChevronRight, TrendingUp
} from 'lucide-react';

const KPICard: React.FC<{
  label: string; value: string | number; sub: string;
  icon: React.ReactNode; color: string; softColor: string; delay: number;
}> = ({ label, value, sub, icon, color, softColor, delay }) => (
  <div className={`animate-in-${delay}`} style={{
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)', padding: '20px 20px',
    position: 'relative', overflow: 'hidden',
  }}>
    <div style={{
      position: 'absolute', top: -20, right: -20, width: 80, height: 80,
      borderRadius: '50%', background: softColor, filter: 'blur(30px)', opacity: 0.6,
    }} />
    <div style={{ position: 'relative' }}>
      <div style={{
        width: 38, height: 38, borderRadius: 'var(--radius-sm)',
        background: softColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 14,
      }}>{icon}</div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 7 }}>{label}</div>
      <div className="kpi-value" style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -1, lineHeight: 1, marginBottom: 7 }}>{value}</div>
      <div style={{ fontSize: 11.5, color, fontWeight: 500 }}>{sub}</div>
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<any>({});
  const [lowStock, setLowStock] = useState<any[]>([]);
  const [rtoStats, setRtoStats] = useState<any>({});
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  const load = () => {
    shopify.getDashboardStats(selectedDate).then(r => setStats(r.data)).catch(() => {});
    inventory.getLowStock().then(r => setLowStock(r.data)).catch(() => {});
    rto.getStats().then(r => setRtoStats(r.data)).catch(() => {});
    shopify.getOrders({ start_date: selectedDate, end_date: selectedDate, limit: 8 })
      .then(r => setRecentOrders(r.data.orders || []))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [selectedDate]);

  const handleSync = async () => {
    setSyncing(true);
    try { await shopify.sync(); load(); } catch (e) {}
    setSyncing(false);
  };

  const shiftDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const kpis = [
    {
      label: 'Orders Received', value: stats.day_orders ?? '—',
      sub: isToday ? 'Today' : formatDate(selectedDate),
      icon: <ShoppingBag size={17} color="var(--accent-blue)" />,
      color: 'var(--accent-blue)', softColor: 'var(--accent-blue-soft)', delay: 1,
    },
    {
      label: 'Fulfilled', value: stats.day_fulfilled ?? '—',
      sub: `Paid & dispatched`,
      icon: <CheckCircle size={17} color="var(--accent-green)" />,
      color: 'var(--accent-green)', softColor: 'var(--accent-green-soft)', delay: 2,
    },
    {
      label: "Day's Revenue", value: stats.day_revenue ? `₹${Number(stats.day_revenue).toLocaleString('en-IN')}` : '—',
      sub: 'Fulfilled + paid',
      icon: <IndianRupee size={17} color="var(--accent-amber)" />,
      color: 'var(--accent-amber)', softColor: 'var(--accent-amber-soft)', delay: 3,
    },
    {
      label: 'COD / Prepaid', value: `${stats.day_cod ?? 0} / ${stats.day_prepaid ?? 0}`,
      sub: 'Payment method split',
      icon: <CreditCard size={17} color="var(--accent-purple)" />,
      color: 'var(--accent-purple)', softColor: 'var(--accent-purple-soft)', delay: 4,
    },
  ];

  const statusColor = (s: string) => {
    switch (s) {
      case 'fulfilled': return { bg: 'var(--accent-blue-soft)', text: 'var(--accent-blue)' };
      case 'delivered': return { bg: 'var(--accent-green-soft)', text: 'var(--accent-green)' };
      case 'received': return { bg: 'var(--accent-amber-soft)', text: 'var(--accent-amber)' };
      default: return { bg: 'var(--bg-elevated)', text: 'var(--text-muted)' };
    }
  };

  return (
    <Layout title="Dashboard" subtitle="Real-time overview of your operations">
      {/* Date Picker Bar */}
      <div className="animate-in" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: '4px 6px',
        }}>
          <button onClick={() => shiftDate(-1)} style={{
            background: 'var(--bg-elevated)', border: 'none', borderRadius: 6,
            width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-secondary)',
          }}><ChevronLeft size={16} /></button>

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Calendar size={14} color="var(--accent-blue)" style={{ marginRight: 8, marginLeft: 6 }} />
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              style={{
                background: 'transparent', border: 'none', color: 'var(--text-primary)',
                fontSize: 13, fontWeight: 600, outline: 'none', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            />
          </div>

          <button onClick={() => shiftDate(1)} disabled={isToday} style={{
            background: isToday ? 'transparent' : 'var(--bg-elevated)', border: 'none', borderRadius: 6,
            width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: isToday ? 'default' : 'pointer',
            color: isToday ? 'var(--text-muted)' : 'var(--text-secondary)',
            opacity: isToday ? 0.3 : 1,
          }}><ChevronRight size={16} /></button>

          {!isToday && (
            <button onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} style={{
              background: 'var(--accent-blue-soft)', border: 'none', borderRadius: 6,
              padding: '6px 12px', fontSize: 11.5, fontWeight: 600,
              color: 'var(--accent-blue)', cursor: 'pointer', marginLeft: 4,
            }}>Today</button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* MTD badge */}
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', padding: '8px 16px',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <TrendingUp size={14} color="var(--accent-green)" />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>MTD</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-green)' }}>
              ₹{(stats.mtd_revenue || 0).toLocaleString('en-IN')}
            </span>
            <span style={{
              fontSize: 10.5, color: 'var(--text-muted)',
              background: 'var(--bg-elevated)', padding: '2px 7px', borderRadius: 10,
            }}>{stats.mtd_orders ?? 0} orders</span>
          </div>

          <button onClick={handleSync} disabled={syncing} style={{
            padding: '9px 18px',
            background: syncing ? 'var(--bg-elevated)' : 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))',
            border: 'none', borderRadius: 'var(--radius-sm)',
            color: '#fff', fontSize: 13, fontWeight: 600,
            cursor: syncing ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 7,
          }}>
            <RefreshCw size={14} style={syncing ? { animation: 'spin 1s linear infinite' } : {}} />
            {syncing ? 'Syncing...' : 'Sync'}
          </button>
        </div>
      </div>

      {/* KPI Grid — responsive */}
      <div className="grid-4" style={{ marginBottom: 22 }}>
        {kpis.map((k, i) => <KPICard key={i} {...k} />)}
      </div>

      {/* Main Grid — responsive */}
      <div className="grid-main">
        {/* Orders for the day */}
        <div className="animate-in-2" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: 0, overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px', borderBottom: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ShoppingBag size={15} color="var(--accent-blue)" />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                Orders — {isToday ? 'Today' : formatDate(selectedDate)}
              </span>
            </div>
            <span style={{
              fontSize: 11, color: 'var(--text-muted)', fontWeight: 500,
              background: 'var(--bg-elevated)', padding: '4px 10px', borderRadius: 20,
            }}>{stats.day_orders ?? 0} orders</span>
          </div>
          <div className="table-responsive">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Order', 'Customer', 'Amount', 'Method', 'Status'].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left', fontSize: 10.5,
                      color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order: any) => {
                  const sc = statusColor(order.status);
                  return (
                    <tr key={order.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--accent-blue)', fontWeight: 600 }}>{order.order_name}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{order.customer_name || '—'}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>₹{(order.total || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{
                          fontSize: 10.5, padding: '3px 8px', borderRadius: 20, fontWeight: 600, textTransform: 'uppercase',
                          background: order.payment_method === 'cod' ? 'var(--accent-amber-soft)' : 'var(--accent-blue-soft)',
                          color: order.payment_method === 'cod' ? 'var(--accent-amber)' : 'var(--accent-blue)',
                        }}>{order.payment_method}</span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{
                          fontSize: 10.5, padding: '3px 8px', borderRadius: 20, fontWeight: 600,
                          textTransform: 'capitalize', background: sc.bg, color: sc.text,
                        }}>{order.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {recentOrders.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
              No orders on this date.
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Low Stock */}
          <div className="animate-in-3" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '18px 20px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={14} color="var(--accent-red)" />
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Low Stock</span>
              </div>
              <span style={{
                fontSize: 10.5, padding: '3px 9px', borderRadius: 20, fontWeight: 600,
                background: lowStock.length > 0 ? 'var(--accent-red-soft)' : 'var(--accent-green-soft)',
                color: lowStock.length > 0 ? 'var(--accent-red)' : 'var(--accent-green)',
              }}>{lowStock.length > 0 ? `${lowStock.length} alerts` : 'All clear'}</span>
            </div>
            {lowStock.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--accent-green)', fontSize: 12.5 }}>
                <CheckCircle size={18} style={{ marginBottom: 6, opacity: 0.7 }} />
                <div>Stock levels healthy</div>
              </div>
            ) : (
              lowStock.slice(0, 4).map((item: any) => (
                <div key={item.id} style={{
                  background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.1)',
                  borderRadius: 'var(--radius-sm)', padding: '9px 12px', marginBottom: 6,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>{item.color} {item.article_type}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--accent-red)', fontWeight: 700 }}>{item.quantity} left</span>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>Size {item.size} · Min {item.notify_below}</div>
                </div>
              ))
            )}
          </div>

          {/* RTO */}
          <div className="animate-in-4" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '18px 20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <RotateCcw size={14} color="var(--accent-purple)" />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>RTO Overview</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {[
                { label: 'Total', value: rtoStats.total ?? 0, color: 'var(--text-primary)' },
                { label: 'Available', value: rtoStats.available ?? 0, color: 'var(--accent-green)' },
                { label: 'Reused', value: rtoStats.used ?? 0, color: 'var(--accent-blue)' },
              ].map((s, i) => (
                <div key={i} style={{
                  background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                  padding: '14px 10px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.color, letterSpacing: -0.5, lineHeight: 1, marginBottom: 5 }}>{s.value}</div>
                  <div style={{ fontSize: 9.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
};

export default Dashboard;
